import type { IngestionPoint } from "./data.ingestion.service";
import type { IngestionPointCategory } from "./data.ingestion.const";
import { InjectionToken } from "@angular/core";

/**
 * Injection token for IngestionSecurityService
 */
export const INGESTION_SECURITY_SERVICE = new InjectionToken<IngestionSecurityService>("IngestionSecurityService");

/**
 * Abstract security service for ingestion forms.
 * Implement this interface to provide custom security checks for ingestion forms.
 * This allows the ingestion system to work with any security framework.
 */
export interface IngestionSecurityService {
    /**
     * Check if the current user can create a form.
     * @param category The form category
     * @param subCategory The form sub-category (e.g., organization ID for ENGAGEMENT forms)
     * @param use The form use configuration (eventSurvey, registration, etc.)
     * @returns true if the user can create the form, false otherwise
     */
    canCreateForm(category: IngestionPointCategory | undefined, subCategory: string | undefined): boolean;

    /**
     * Check if the current user can manage (edit/delete) a form.
     * @param form The ingestion point form
     * @returns true if the user can manage the form, false otherwise
     */
    canManageForm(form: IngestionPoint): boolean;

    /**
     * Check if the current user can view a form.
     * @param form The ingestion point form
     * @returns true if the user can view the form, false otherwise
     */
    canViewForm(form: IngestionPoint): boolean;
}

/**
 * Default implementation that always allows access.
 * Use this for projects without security requirements.
 */
export class PermissiveIngestionSecurityService implements IngestionSecurityService {
    canCreateForm(): boolean {
        return true;
    }

    canManageForm(): boolean {
        return true;
    }

    canViewForm(): boolean {
        return true;
    }
}
